// @ts-strict-ignore
import { useMutation } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { Box, Option } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import CmsToggle from "../../cms/components/CmsToggle";
import { WHATSAPP_TEMPLATE_CREATE_MUTATION, WHATSAPP_TEMPLATE_UPDATE_MUTATION } from "../waMutations";
import { WHATSAPP_TEMPLATES_QUERY } from "../waQueries";
import { WhatsAppTemplate } from "../types";

interface TemplateDialogProps {
  open: boolean;
  template?: WhatsAppTemplate | null;
  onClose: () => void;
}

const CATEGORY_OPTIONS: Option[] = [
  { label: "Marketing (Promotional)", value: "MARKETING" },
  { label: "Utility (Order, delivery notifications)", value: "UTILITY" },
  { label: "Authentication (OTP)", value: "AUTHENTICATION" },
];

export const TemplateDialog: React.FC<TemplateDialogProps> = ({ open, template, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!template;

  const [name, setName] = useState(template?.name || "");
  const [displayName, setDisplayName] = useState(template?.displayName || "");
  const [category, setCategory] = useState<string>(
    template?.category || "MARKETING"
  );
  const [languageCode, setLanguageCode] = useState(template?.languageCode || "en_US");
  const [parameterSchema, setParameterSchema] = useState(
    template?.parameterSchema
      ? typeof template.parameterSchema === "string"
        ? template.parameterSchema
        : JSON.stringify(template.parameterSchema, null, 2)
      : JSON.stringify([{ name: "customer_name", type: "text" }], null, 2)
  );
  const [isActive, setIsActive] = useState(template ? template.isActive : true);

  const [pasteText, setPasteText] = useState("");
  const [headerType, setHeaderType] = useState<"NONE" | "IMAGE" | "VIDEO" | "DOCUMENT">("NONE");

  const handleAutoDetect = (text: string, hType: "NONE" | "IMAGE" | "VIDEO" | "DOCUMENT" = headerType) => {
    setPasteText(text);
    const matches = Array.from(text.matchAll(/\{\{(\d+)\}\}/g)).map(m => m[1]);
    const unique = Array.from(new Set(matches)).sort((a, b) => Number(a) - Number(b));
    const schema: Record<string, string> = {};
    if (hType === "IMAGE") schema["header_image"] = "Header Image URL (from Media Library)";
    if (hType === "VIDEO") schema["header_video"] = "Header Video URL (from Media Library)";
    if (hType === "DOCUMENT") schema["header_document"] = "Header Document/PDF URL (from Media Library)";
    unique.forEach(num => {
      schema[num] = `Variable {{${num}}}`;
    });
    if (Object.keys(schema).length > 0) {
      setParameterSchema(JSON.stringify(schema, null, 2));
    }
  };

  React.useEffect(() => {
    if (template) {
      setName(template.name || "");
      setDisplayName(template.displayName || "");
      setCategory(template.category || "MARKETING");
      setLanguageCode(template.languageCode || "en_US");
      setParameterSchema(
        template.parameterSchema
          ? typeof template.parameterSchema === "string"
            ? template.parameterSchema
            : JSON.stringify(template.parameterSchema, null, 2)
          : JSON.stringify({ "1": "Variable {{1}}" }, null, 2)
      );
      setIsActive(template.isActive ?? true);
    } else {
      setName("");
      setDisplayName("");
      setCategory("MARKETING");
      setLanguageCode("en_US");
      setParameterSchema(JSON.stringify({ "1": "Variable {{1}}" }, null, 2));
      setIsActive(true);
      setPasteText("");
      setHeaderType("NONE");
    }
  }, [template, open]);

  const [createTemplate, { loading: creating }] = useMutation(WHATSAPP_TEMPLATE_CREATE_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_TEMPLATES_QUERY }],
    onCompleted: data => {
      if (data.whatsappTemplateCreate?.errors?.length) {
        notify({ status: "error", text: data.whatsappTemplateCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "WhatsApp template registered" });
        onClose();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const [updateTemplate, { loading: updating }] = useMutation(WHATSAPP_TEMPLATE_UPDATE_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_TEMPLATES_QUERY }],
    onCompleted: data => {
      if (data.whatsappTemplateUpdate?.errors?.length) {
        notify({ status: "error", text: data.whatsappTemplateUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "WhatsApp template updated" });
        onClose();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const handleSubmit = () => {
    if (!name || !displayName) {
      notify({ status: "error", text: "Meta template name and display name are required" });
      return;
    }

    try {
      JSON.parse(parameterSchema);
    } catch {
      notify({ status: "error", text: "Invalid JSON in parameter schema" });
      return;
    }

    const input: any = {
      name,
      displayName,
      category,
      languageCode,
      parameterSchema,
      isActive,
      status: "APPROVED",
    };

    if (isEdit) {
      updateTemplate({ variables: { id: template.id, input } });
    } else {
      createTemplate({ variables: { input } });
    }
  };

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit WhatsApp Template" : "Register Approved Meta WhatsApp Template"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        {/* Tip banner */}
        <Box
          padding={3}
          style={{
            backgroundColor: "#eff6ff",
            borderRadius: 8,
            border: "1px solid #bfdbfe",
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#1e40af" }}>
            💡 Zero-Typing Tip: Use "Sync from Meta"
          </Typography>
          <Typography variant="caption" style={{ color: "#2563eb", display: "block", marginTop: 4 }}>
            You don't have to register approved templates manually! On the Templates page, click the <strong>"Sync from Meta"</strong> button to automatically import all approved templates with their parameters in 1 click.
          </Typography>
        </Box>

        {/* Smart Auto-Detect Helper */}
        <Box
          padding={3}
          style={{
            backgroundColor: "#f8fafc",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#334155" }}>
            ✨ Smart Auto-Detect (Paste Meta Message Text)
          </Typography>
          <TextField
            label="Paste Meta Template Body Text"
            placeholder="e.g. Hello {{1}}, fresh Alphonso mangoes have arrived! Use code {{2}} at checkout."
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            size="small"
            value={pasteText}
            onChange={e => handleAutoDetect(e.target.value)}
            helperText="Pasting message text automatically detects {{1}}, {{2}} and creates the parameter schema below."
          />

          <FormControl component="fieldset">
            <FormLabel component="legend" style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Header Media (Optional)
            </FormLabel>
            <RadioGroup
              row
              value={headerType}
              onChange={e => {
                const val = e.target.value as any;
                setHeaderType(val);
                handleAutoDetect(pasteText, val);
              }}
            >
              <FormControlLabel value="NONE" control={<Radio size="small" />} label="None (Text Only)" />
              <FormControlLabel value="IMAGE" control={<Radio size="small" />} label="Image" />
              <FormControlLabel value="VIDEO" control={<Radio size="small" />} label="Video" />
              <FormControlLabel value="DOCUMENT" control={<Radio size="small" />} label="Document (PDF)" />
            </RadioGroup>
          </FormControl>
        </Box>

        <TextField
          label="Meta Template Name (exact identifier in Meta Manager)"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={isEdit}
          helperText="e.g. kb_order_confirmation_1, kb_diwali_promo, kb_weekly_basket"
          required
        />
        <TextField
          label="Display Name (Staff readable title)"
          fullWidth
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          helperText="e.g. Diwali Sweets Promo 2026"
          required
        />

        <Select
          label="Category"
          name="category"
          value={category}
          options={CATEGORY_OPTIONS}
          onChange={({ target }) => setCategory(target.value)}
        />

        <TextField
          label="Language Code"
          fullWidth
          value={languageCode}
          onChange={e => setLanguageCode(e.target.value)}
          helperText="e.g. en_US, mr, hi"
        />
        <TextField
          label="Parameter Schema (JSON list of required fields)"
          fullWidth
          multiline
          rows={4}
          value={parameterSchema}
          onChange={e => setParameterSchema(e.target.value)}
          helperText='List of template parameter objects: [{"name": "customer_name", "type": "text"}]'
        />

        <CmsToggle
          label="Active for Campaigns"
          description="Allow staff to select and broadcast this approved template"
          checked={isActive}
          onChange={setIsActive}
        />
      </Box>
    </ActionDialog>
  );
};

export default TemplateDialog;
